import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import { Diagram3Fill, FileEarmarkTextFill } from 'react-bootstrap-icons';

import { useProjects } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { ProjectWithRepository, RepositorySummary, RepositoryArtifact } from '../types/project';
import {
  fetchRepositoryDetails,
  fetchRepositoryInputTree,
  fetchRepositoryOutputTree,
  fetchRepositoryMetrics,
  fetchRepositoryFileContent,
  fetchRepositoryLogs,
  queueTestGeneration,
  downloadRepositoryZip,
  fetchRepositoryTestGenerations,
} from '../api/repositories';
import apiClient, { API_ENDPOINTS } from '../api/config';
import { formatLocalDateTimeFromUTC } from '../utils/datetime';
import CodeViewer from '../components/CodeViewer';
import { logDownloadFilename, triggerDownloadFromBlob, triggerDownloadFromUrl } from '../utils/download';
import { RepositoryTestGenerationSummary } from '../types/repository';

type FileItem = {
  path: string;
  name: string;
  size?: number;
  type?: 'file' | 'dir';
  mtime?: string;
  analyzed?: boolean;
  analyzed_at?: string;
  children?: FileItem[];
  file_count?: number;
};

const statusVariant = (status?: string) => {
  switch ((status || '').toLowerCase()) {
    case 'completed':
      return 'success';
    case 'running':
      return 'info';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'secondary';
  }
};

const LIVE_LOG_MAX_BYTES = 128 * 1024;

const RepositoryView: React.FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId?: string }>();
  const numericProjectId = Number(projectId ?? NaN);
  const {
    refreshProject,
  } = useProjects();
  const { user } = useAuth();

  const [project, setProject] = useState<ProjectWithRepository | null>(null);
  const [repoDetails, setRepoDetails] = useState<RepositorySummary | null>(null);
  const [inputTree, setInputTree] = useState<FileItem[]>([]);
  const [outputTree, setOutputTree] = useState<FileItem[]>([]);
  const [showAnalyzedOnly, setShowAnalyzedOnly] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLogs, setShowLogs] = useState(false);
  const [logsTitle, setLogsTitle] = useState('');
  const [logsContent, setLogsContent] = useState('');
  const [logDownloadUrl, setLogDownloadUrl] = useState<string | null>(null);
  const [actionRunning, setActionRunning] = useState(false);
  const [metrics, setMetrics] = useState<{ files_analyzed?: number | null; apis_found?: number | null; tests_generated?: number | null } | null>(null);
  const [jobs, setJobs] = useState<RepositoryTestGenerationSummary[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<{ scope: 'input' | 'output'; path: string; name: string } | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [fileLoading, setFileLoading] = useState(false);
  const canViewLogs = user?.can_view_logs ?? true;

  const repositoryId = useMemo(() => project?.repository?.id ?? null, [project]);

  const fetchProjectAndRepository = useCallback(async () => {
    if (!Number.isFinite(numericProjectId)) {
      setError('Invalid project id');
      setLoading(false);
      setJobsLoading(false);
      return;
    }
    setLoading(true);
    setJobsLoading(true);
    setError(null);
    setJobsError(null);
    try {
      const fetchedProject = await refreshProject(numericProjectId);
      setProject(fetchedProject ?? null);
      if (!fetchedProject?.repository?.id) {
        setLoading(false);
        setJobsLoading(false);
        setJobs([]);
        return;
      }

      const repoId = fetchedProject.repository.id;
      const [details, input, output, metricsRes] = await Promise.all([
        fetchRepositoryDetails(repoId),
        fetchRepositoryInputTree(repoId).catch(() => [] as FileItem[]),
        fetchRepositoryOutputTree(repoId).catch(() => [] as FileItem[]),
        fetchRepositoryMetrics(repoId).catch(() => null),
      ]);

      let jobsData: RepositoryTestGenerationSummary[] = [];
      try {
        jobsData = await fetchRepositoryTestGenerations(repoId);
        setJobsError(null);
      } catch (jobsErr: any) {
        const jobMessage = jobsErr?.response?.data?.detail || jobsErr?.message || 'Failed to load recent jobs';
        setJobsError(jobMessage);
        jobsData = [];
      }

      setRepoDetails(details);
      setInputTree(input as FileItem[]);
      setOutputTree(output as FileItem[]);
      setMetrics(metricsRes);
      setJobs(jobsData);
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Failed to load repository';
      setError(message);
    } finally {
      setLoading(false);
      setJobsLoading(false);
    }
  }, [numericProjectId, refreshProject]);

  useEffect(() => {
    fetchProjectAndRepository();
  }, [fetchProjectAndRepository]);

  const handleGenerateTests = async () => {
    if (!repositoryId || !project) return;
    setActionRunning(true);
    try {
      await queueTestGeneration(repositoryId, project.id);
      await fetchProjectAndRepository();
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Failed to queue test generation';
      setError(message);
    } finally {
      setActionRunning(false);
    }
  };

  const handleViewLogs = async () => {
    if (!repositoryId || !project?.repository) return;
    const downloadUrl = findArtifactBlobUrl('workflow_log');
    setLogDownloadUrl(downloadUrl ?? null);
    setLogsTitle(`${project.repository.name} - workflow.log`);
    setLogsContent('Loading logs...');
    setShowLogs(true);
    try {
      const data = await fetchRepositoryLogs(repositoryId);
      setLogsContent(data?.content ?? '');
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Log file not found or failed to load';
      setLogsContent(message);
      setLogDownloadUrl(null);
    }
  };

  const fetchJobLog = useCallback(async (job: Pick<RepositoryTestGenerationSummary, 'id' | 'status'>) => {
    const isActive = ['pending', 'running'].includes((job.status || '').toLowerCase());
    if (isActive) {
      const response = await apiClient.get(API_ENDPOINTS.TEST_GENERATION_LIVE_LOG(job.id), {
        params: { max_bytes: LIVE_LOG_MAX_BYTES },
      });
      return {
        content: response.data?.content ?? '',
        path: response.data?.path,
        status: response.data?.status ?? job.status,
        source: response.data?.source ?? 'pending',
      };
    }

    const response = await apiClient.get(API_ENDPOINTS.TEST_GENERATION_LOGS(job.id));
    return {
      content: response.data?.content ?? '',
      path: response.data?.path,
      status: job.status,
      source: 'persisted',
    };
  }, []);

  const downloadFromEndpoint = async (endpoint: string, fallbackName: string) => {
    try {
      const blob = await downloadRepositoryZip(endpoint);
      triggerDownloadFromBlob(blob, fallbackName);
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Download failed';
      setError(message);
    }
  };

  const downloadDisplayedLog = () => {
    const repoName = repository?.name || project?.repository?.name || 'repository';
    const blob = new Blob([logsContent || ''], { type: 'text/plain;charset=utf-8' });
    triggerDownloadFromBlob(blob, logDownloadFilename(`${repoName}_workflow.log`));
  };

  const findArtifactBlobUrl = (artifactType: string): string | undefined => {
    const artifactList = repoDetails?.artifacts ?? project?.repository?.artifacts ?? [];
    const match = artifactList.find((item: RepositoryArtifact) => item.artifact_type === artifactType);
    return match?.blobs?.[0]?.download_url || undefined;
  };

  const handleDownloadInput = async () => {
    if (!repository) return;
    const filename = `${repository.name}_input.zip`;
    if (repoDetails?.input_download_url) {
      triggerDownloadFromUrl(repoDetails.input_download_url, filename);
      return;
    }
    await downloadFromEndpoint(
      API_ENDPOINTS.REPOSITORY_DOWNLOAD_INPUT_ZIP(String(repository.id)),
      filename,
    );
  };

  const handleDownloadOutput = async () => {
    if (!repository) return;
    const filename = `${repository.name}_output.zip`;
    const blobUrl = findArtifactBlobUrl('workflow_output');
    if (blobUrl) {
      triggerDownloadFromUrl(blobUrl, filename);
      return;
    }
    await downloadFromEndpoint(
      API_ENDPOINTS.REPOSITORY_DOWNLOAD_OUTPUT_ZIP(String(repository.id)),
      filename,
    );
  };

  const handleRefreshJobs = async () => {
    if (!repositoryId) return;
    setJobsLoading(true);
    try {
      const data = await fetchRepositoryTestGenerations(repositoryId);
      setJobs(data);
      setJobsError(null);
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Failed to refresh jobs';
      setJobsError(message);
    } finally {
      setJobsLoading(false);
    }
  };

  const handleViewJobLogs = async (job: RepositoryTestGenerationSummary) => {
    const repoName = project?.repository?.name ?? 'repository';
    setLogsTitle(`${repoName} - workflow.log (Job #${job.id})`);
    setLogsContent('Loading logs...');
    setLogDownloadUrl(null);
    setShowLogs(true);
    try {
      const response = await fetchJobLog(job);
      setLogsContent(response.content ?? '');
      const path = response.path;
      if (path && typeof path === 'string' && path.startsWith('http')) {
        setLogDownloadUrl(path);
      }
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Log file not found or failed to load';
      setLogsContent(message);
    }
  };

  const loadFileContent = async (scope: 'input' | 'output', path: string, name: string) => {
    if (!repositoryId) return;
    setSelectedFile({ scope, path, name });
    setFileLoading(true);
    try {
      const res = await fetchRepositoryFileContent(repositoryId, scope, path);
      setFileContent(res?.content || '');
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Failed to load file';
      setFileContent(message);
    } finally {
      setFileLoading(false);
    }
  };

  if (!Number.isFinite(numericProjectId)) {
    return <Alert variant="danger">Invalid project identifier.</Alert>;
  }

  if (loading) {
    return (
      <div className="text-center" style={{ padding: '100px 20px' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}
        <div className="mt-2">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(`/projects/${numericProjectId}`)}>Back to Project</Button>
        </div>
      </Alert>
    );
  }

  if (!project || !project.repository || !repositoryId) {
    return (
      <Alert variant="warning">
        This project does not have an attached repository yet.
        <div className="mt-2">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(`/projects/${numericProjectId}`)}>Back to Project</Button>
        </div>
      </Alert>
    );
  }

  const repository = project.repository;
  const inputChecksum = repoDetails?.input_checksum ?? repository.input_checksum ?? null;

  const fileIcon = (name: string, type?: string) => {
    if (type === 'dir') return '📁';
    const lower = name.toLowerCase();
    if (lower.endsWith('.ts') || lower.endsWith('.tsx') || lower.endsWith('.js') || lower.endsWith('.jsx')) return '🧩';
    if (lower.endsWith('.py')) return '🐍';
    if (lower.endsWith('.cs')) return '⚙️';
    if (lower.endsWith('.json')) return '🗂️';
    if (lower.endsWith('.md')) return '📝';
    if (lower.endsWith('.yml') || lower.endsWith('.yaml')) return '🧰';
    if (lower.endsWith('.xml')) return '📄';
    return '📄';
  };

  const filterNodes = (items: FileItem[]): FileItem[] => {
    return items
      .map((item) => {
        let children = item.children || [];
        if (children.length > 0) {
          children = filterNodes(children);
        }
        const matchesSearch = searchText
          ? item.name.toLowerCase().includes(searchText.toLowerCase()) || children.length > 0
          : true;
        const matchesAnalyzed = showAnalyzedOnly ? Boolean(item.analyzed) : true;
        if (matchesSearch && matchesAnalyzed) {
          return { ...item, children };
        }
        return null;
      })
      .filter(Boolean) as FileItem[];
  };

  const renderTree = (items: FileItem[], scope: 'input' | 'output', basePath = '') => {
    if (!items || items.length === 0) {
      return <div className="text-muted">No files</div>;
    }
    const filtered = filterNodes(items);
    return (
      <ul className="list-unstyled ms-3">
        {filtered.map((item) => {
          const fullPath = `${basePath}/${item.name}`;
          const isDir = item.type === 'dir' || (item.children && item.children.length > 0);
          const isExpanded = expandedPaths[fullPath];
          return (
            <li key={fullPath} className="mb-1">
              <div className="d-flex align-items-center gap-2">
                {isDir ? (
                  <Button variant="link" size="sm" className="p-0" onClick={() => setExpandedPaths(prev => ({ ...prev, [fullPath]: !isExpanded }))}>
                    {isExpanded ? '▼' : '▶'}
                  </Button>
                ) : <span style={{ width: 16 }}></span>}
                <span style={{ cursor: isDir ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (!isDir) {
                      loadFileContent(scope, fullPath.replace(/^\//, ''), item.name);
                    }
                  }}>
                  {fileIcon(item.name, item.type)} {item.name}
                  {item.analyzed && <Badge bg="success" className="ms-2">Analyzed</Badge>}
                </span>
              </div>
              {isDir && isExpanded && renderTree(item.children || [], scope, fullPath)}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="d-flex flex-column gap-3">
      <Breadcrumb>
        <Breadcrumb.Item onClick={() => navigate('/projects')}>Projects</Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => navigate(`/projects/${project.id}`)}>{project.name}</Breadcrumb.Item>
        <Breadcrumb.Item active>Repository</Breadcrumb.Item>
      </Breadcrumb>

      <Card className="shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="mb-1">{repository.name}</h3>
              <div className="text-muted">Project: {project.name}</div>
            </div>
            <Badge bg={statusVariant(repository.status)} className="text-uppercase">{repository.status}</Badge>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col md={8}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Repository status</h5>
                <div className="d-flex gap-2">
                  {canViewLogs && (
                    <Button variant="outline-secondary" size="sm" onClick={handleViewLogs}>View Logs</Button>
                  )}
                  <Button variant="outline-primary" size="sm" onClick={() => navigate(`/projects/${project.id}/repository/results`)}>
                    View Results
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleGenerateTests} disabled={actionRunning}>
                    {actionRunning ? <Spinner animation="border" size="sm" /> : 'Generate Tests'}
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={handleDownloadInput}>Download Input</Button>
                  <Button variant="outline-secondary" size="sm" onClick={handleDownloadOutput}>Download Output</Button>
                </div>
              </div>

              <Row className="g-2">
                <Col sm={4}>
                  <Card className="bg-light border-0">
                    <Card.Body>
                      <div className="text-muted small">Files Analyzed</div>
                      <div className="fw-bold" style={{ fontSize: 22 }}><Diagram3Fill className="me-1" />{metrics?.files_analyzed ?? repository.metric_code_files_analyzed ?? 0}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={4}>
                  <Card className="bg-light border-0">
                    <Card.Body>
                      <div className="text-muted small">APIs Found</div>
                      <div className="fw-bold" style={{ fontSize: 22 }}>{metrics?.apis_found ?? repository.metric_apis_found ?? 0}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={4}>
                  <Card className="bg-light border-0">
                    <Card.Body>
                      <div className="text-muted small">Tests Generated</div>
                      <div className="fw-bold" style={{ fontSize: 22 }}><FileEarmarkTextFill className="me-1" />{metrics?.tests_generated ?? repository.metric_tests_generated ?? 0}</div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Project files</h5>
                <div className="d-flex gap-2">
                  <Form.Control
                    size="sm"
                    placeholder="Search files"
                    style={{ maxWidth: 200 }}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Analyzed only"
                    checked={showAnalyzedOnly}
                    onChange={(e) => setShowAnalyzedOnly(e.currentTarget.checked)}
                  />
                </div>
              </div>

              <Row>
                <Col md={6}>
                  <h6 className="text-muted">Input</h6>
                  {renderTree(inputTree, 'input')}
                </Col>
                <Col md={6}>
                  <h6 className="text-muted">Output</h6>
                  {renderTree(outputTree, 'output')}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Recent Jobs</h5>
                <Button variant="outline-primary" size="sm" onClick={handleRefreshJobs} disabled={jobsLoading || !repositoryId}>
                  {jobsLoading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
                </Button>
              </div>
              {jobsError && <Alert variant="warning" className="mb-3">{jobsError}</Alert>}
              {jobsLoading && jobs.length === 0 ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-muted small">No jobs found for this repository.</div>
              ) : (
                <div className="table-responsive">
                  <Table hover responsive size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Status</th>
                        <th>Progress</th>
                        <th>Created</th>
                        <th>Completed</th>
                        <th>Stats</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((job) => {
                        const status = job.status || 'unknown';
                        const created = job.created_at ? formatLocalDateTimeFromUTC(job.created_at) : '—';
                        const completed = job.completed_at ? formatLocalDateTimeFromUTC(job.completed_at) : '—';
                        const stats: string[] = [];
                        if (typeof job.test_count === 'number') {
                          stats.push(`${job.test_count} tests`);
                        }
                        if (typeof job.coverage_percentage === 'number') {
                          stats.push(`${job.coverage_percentage}% coverage`);
                        }
                        return (
                          <tr key={job.id}>
                            <td>{job.id}</td>
                            <td><Badge bg={statusVariant(status)} className="text-uppercase">{status}</Badge></td>
                            <td>{typeof job.progress === 'number' ? `${job.progress}%` : '—'}</td>
                            <td>{created}</td>
                            <td>{completed}</td>
                            <td>{stats.length > 0 ? stats.join(' · ') : <span className="text-muted">—</span>}</td>
                            <td className="d-flex flex-wrap gap-2">
                              {canViewLogs && (
                                <Button variant="outline-secondary" size="sm" onClick={() => handleViewJobLogs(job)}>
                                  View Logs
                                </Button>
                              )}
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => navigate(`/projects/${project.id}/repository/results`)}
                                disabled={(job.status || '').toLowerCase() !== 'completed'}
                              >
                                View Results
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="shadow-sm">
            <Card.Body>
              <h5 className="mb-3">File preview</h5>
              {selectedFile ? (
                fileLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <CodeViewer filename={selectedFile.name || selectedFile.path} content={fileContent} />
                )
              ) : (
                <div className="text-muted">Select a file to preview its contents.</div>
              )}
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Body>
              <h6 className="mb-2">Repository metadata</h6>
              <div className="small text-muted">Uploaded {repository.created_at ? formatLocalDateTimeFromUTC(repository.created_at) : 'recently'}</div>
              <div className="small text-muted">Status updated {repoDetails?.updated_at ? formatLocalDateTimeFromUTC(repoDetails.updated_at) : '-'}</div>
              {inputChecksum && <div className="small text-muted">Input checksum: {inputChecksum.slice(0, 12)}…</div>}
              {repoDetails?.input_download_url && (
                <div className="small">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={() => triggerDownloadFromUrl(repoDetails.input_download_url!, `${repository.name}_input.zip`)}
                  >
                    Download input archive
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showLogs} onHide={() => { setShowLogs(false); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{logsTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 500, overflowY: 'auto' }}>
          <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{logsContent}</pre>
        </Modal.Body>
        <Modal.Footer>
          {logDownloadUrl && logsContent && logsContent !== 'Loading logs...' && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={downloadDisplayedLog}
            >
              Download Log
            </Button>
          )}
          <Button variant="outline-primary" size="sm" onClick={() => setShowLogs(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RepositoryView;
